import { noop } from 'lodash-es';
import { useCallback, useEffect, useMemo } from 'react';
import { ParcelConfigObject } from 'single-spa';

import services from 'services';
import { MainErrorItem, ModuleMeta } from 'types';
import { deprecatedMethod, findModule, isWidgetSidebarItem, logger } from 'utils';

import { useModuleLoader } from './loader';
import type { ModuleState } from './loader/ModuleLoaderProvider';
import { useModules } from './modules';

const initModuleState: ModuleState = {
  loading: false,
  error: null,
  module: null,
  meta: null,
};

type UseParcelResult = {
  parcel: ParcelConfigObject | null;
  error: MainErrorItem | null;
  loading: boolean;
  load: (force?: boolean) => Promise<void>;
  unload: () => void;
  reload: () => void;
  widgetConfig: any;
  meta: ModuleMeta | null;
};
/**
 *
 * @param {*} moduleKey
 * @returns
 */
const useParcel = (moduleKey): UseParcelResult => {
  const { routedModules } = useModules();
  const { load: loadModule, unload: unloadModule, reload: reloadModule, modulesMap, getModulesMap } = useModuleLoader();

  const load = useCallback(async (force = false) => loadModule(moduleKey, { force }), [moduleKey, loadModule]);
  const unload = useCallback(() => unloadModule(moduleKey), [moduleKey, unloadModule]);
  const reload = useCallback(() => reloadModule(moduleKey), [moduleKey, reloadModule]);

  const sidebarItem = useMemo(() => findModule(routedModules, moduleKey)?.module, [routedModules, moduleKey]);
  const widget = (sidebarItem && isWidgetSidebarItem(sidebarItem) && sidebarItem.widget) || null;
  const deprecatedConfigs = sidebarItem?.configs;
  if (deprecatedConfigs) {
    deprecatedMethod(noop, 'config.modules[].configs', 'config.modules[].config');
  }
  const widgetConfig = sidebarItem?.config || deprecatedConfigs || null;

  const moduleState = modulesMap[moduleKey] || initModuleState;

  useEffect(() => {
    if (widget) {
      return undefined;
    }
    try {
      loadModule(moduleKey);
    } catch (err) {
      logger.error(`Unable to load parcel: ${moduleKey}`, err);
    }
    return () => {
      // unload module when in error state (as error is also cached)
      if (getModulesMap()[moduleKey].error) {
        unloadModule(moduleKey);
      }
    };
  }, [moduleKey, loadModule, getModulesMap, unloadModule, widget]);

  if (widget) {
    const parcel = services.getWidget(widget);
    return {
      parcel,
      error: parcel
        ? null
        : {
            title: 'errors.module_load_error.title',
            message: 'errors.common_message',
            details: {
              type: 'WIDGET_NOT_FOUND',
              timestamp: new Date(),
              message: 'Widget not found in widget registry!',
              widget,
            },
          },
      loading: false,
      widgetConfig,
      meta: null,
      load: noop as UseParcelResult['load'],
      unload: noop,
      reload: noop,
    };
  }

  const { module, error, loading, meta } = moduleState;
  return { parcel: module as ParcelConfigObject, error, loading, load, unload, reload, widgetConfig, meta };
};

export default useParcel;
